import { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { getPlansWithStatus, renewSlot } from '../../api/planApi';
import { getMyWallets } from '../../api/walletApi';

const cyan = '#00e5ff';
const orange = '#ffaa44';
const cardDark = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(255,170,68,0.08) 0%, #0d0d0d 65%)',
  border: '1px solid rgba(255,170,68,0.25)',
  color: '#fff',
};

const InfoRow = ({ label, value, valueStyle }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
    <span style={{ color: '#888', fontSize: 13 }}>{label}</span>
    <span style={{ fontWeight: 600, fontSize: 15, ...valueStyle }}>{value}</span>
  </div>
);

const WALLETS = [
  { key: 'main_wallet', label: 'Main Wallet' },
  { key: 'one_wallet',  label: 'One Wallet'  },
];

const RenewSlot = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [walletBalances, setWalletBalances] = useState({ main_wallet: 0, one_wallet: 0 });
  const [selectedWallet, setSelectedWallet] = useState('main_wallet');
  const [loading, setLoading] = useState(true);
  const [renewing, setRenewing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [renewalCount, setRenewalCount] = useState(null);

  useEffect(() => {
    Promise.all([getPlansWithStatus(), getMyWallets()])
      .then(([plansRes, walletsRes]) => {
        if (!plansRes.data.renewalFeatureEnabled) { navigate('/slots'); return; }
        const found = plansRes.data.plans.find((p) => p._id === planId);
        if (!found || !found.purchased) { navigate('/slots'); return; }
        setPlan(found);
        const w = walletsRes.data.wallets;
        setWalletBalances({
          main_wallet: w.main_wallet?.balance ?? 0,
          one_wallet:  w.one_wallet?.balance  ?? 0,
        });
      })
      .finally(() => setLoading(false));
  }, [planId, navigate]);

  const otherWalletKey   = selectedWallet === 'main_wallet' ? 'one_wallet' : 'main_wallet';
  const otherWalletLabel = otherWalletKey === 'one_wallet' ? 'One Wallet' : 'Main Wallet';
  const selectedLabel    = selectedWallet === 'one_wallet' ? 'One Wallet' : 'Main Wallet';
  const selectedBalance  = walletBalances[selectedWallet];
  const otherBalance     = walletBalances[otherWalletKey];
  const combinedBalance  = parseFloat((selectedBalance + otherBalance).toFixed(2));
  const needsSplit       = plan && selectedBalance > 0 && selectedBalance < plan.amount && combinedBalance >= plan.amount;
  const insufficient     = plan && (selectedBalance <= 0 || combinedBalance < plan.amount);
  const amountFromPrimary   = !plan ? 0 : needsSplit ? selectedBalance : Math.min(selectedBalance, plan.amount);
  const amountFromSecondary = needsSplit ? parseFloat((plan.amount - selectedBalance).toFixed(2)) : 0;

  const handleRenew = async () => {
    setError('');
    setRenewing(true);
    try {
      const res = await renewSlot(planId, selectedWallet);
      if (res.data.success) {
        setBreakdown(res.data.breakdown || null);
        setRenewalCount(res.data.renewalCount);
        setSuccess(true);
        setTimeout(() => navigate('/slots'), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Renewal failed');
    } finally {
      setRenewing(false);
    }
  };

  if (loading) return (
    <Container className="mt-5 text-center">
      <Spinner animation="border" style={{ color: orange }} />
    </Container>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4" style={{ maxWidth: 480 }}>
        <button
          onClick={() => navigate('/slots')}
          style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}
        >
          ‹ Back to Slots
        </button>

        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Confirm Renewal</h5>
        {plan && (
          <div style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>
            Renewing <span style={{ color: orange, fontWeight: 600 }}>{plan.planName}</span> →
            <span style={{ color: orange, fontWeight: 700 }}> R{plan.nextRenewalCount}</span>
          </div>
        )}

        {success ? (
          <Card style={cardDark} className="p-4 text-center">
            <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
            <div style={{ color: '#66ff99', fontWeight: 700, fontSize: 16 }}>
              {plan?.planName} Renewed — R{renewalCount}!
            </div>

            {breakdown?.length > 0 && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 16px', marginTop: 16, textAlign: 'left' }}>
                <div style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  {breakdown.length > 1 ? 'Payment Breakdown' : 'Paid From'}
                </div>
                {breakdown.map((b) => (
                  <div key={b.wallet} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#aaa', fontSize: 13 }}>{b.wallet}</span>
                    <span style={{ color: '#66ff99', fontSize: 13, fontWeight: 700 }}>{b.amount.toFixed(2)} USDT</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ color: '#888', fontSize: 13, marginTop: 8 }}>Redirecting to slots...</div>
          </Card>
        ) : (
          <Card style={cardDark} className="p-4">
            {plan && (
              <>
                <InfoRow label="Slot Name"      value={plan.planName} valueStyle={{ color: '#fff' }} />
                <InfoRow label="Renewal Round"  value={`R${plan.nextRenewalCount}`} valueStyle={{ color: orange }} />
                <InfoRow label="Renewal Amount" value={`${plan.amount?.toFixed(2)} USDT`} valueStyle={{ color: '#66ff99' }} />
                <InfoRow
                  label="Earning Threshold"
                  value={`${plan.renewalThreshold?.toFixed(2)} USDT`}
                  valueStyle={{ color: '#aaa', fontSize: 13 }}
                />
                <InfoRow
                  label="Your Earnings"
                  value={`${plan.totalEarningCredits?.toFixed(2)} USDT`}
                  valueStyle={{ color: plan.totalEarningCredits >= plan.renewalThreshold ? '#66ff99' : '#ff6666', fontSize: 13 }}
                />
              </>
            )}

            {/* Wallet selector */}
            <div style={{ padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ color: '#888', fontSize: 13, marginBottom: 10 }}>Pay from</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {WALLETS.map(({ key, label }) => {
                  const bal = walletBalances[key];
                  const active = selectedWallet === key;
                  const low = bal < (plan?.amount ?? 0);
                  const willAutoTopUp = active && low && !insufficient;
                  return (
                    <div
                      key={key}
                      onClick={() => setSelectedWallet(key)}
                      style={{
                        border: active ? `1.5px solid ${orange}` : '1.5px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        padding: '10px 12px',
                        cursor: 'pointer',
                        background: active ? 'rgba(255,170,68,0.07)' : 'rgba(255,255,255,0.02)',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ fontSize: 12, color: active ? orange : '#888', fontWeight: 600, marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: low ? (willAutoTopUp ? orange : '#ff6666') : '#fff' }}>
                        {Number(bal).toFixed(2)} USDT
                      </div>
                      {low && (willAutoTopUp
                        ? <div style={{ fontSize: 10, color: orange, marginTop: 2 }}>Will auto top-up from {otherWalletLabel}</div>
                        : <div style={{ fontSize: 10, color: '#ff6666', marginTop: 2 }}>Insufficient</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {needsSplit && (
              <div style={{ background: 'rgba(255,170,68,0.06)', border: '1px solid rgba(255,170,68,0.2)', borderRadius: 8, padding: '12px 16px', marginTop: 4 }}>
                <div style={{ color: orange, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  Combined Payment — Auditable Breakdown
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span style={{ color: '#aaa', fontSize: 13 }}>{selectedLabel} (entire balance)</span>
                  <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{amountFromPrimary.toFixed(2)} USDT</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span style={{ color: '#aaa', fontSize: 13 }}>{otherWalletLabel} (auto top-up)</span>
                  <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{amountFromSecondary.toFixed(2)} USDT</span>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0' }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Total Payable</span>
              <span style={{ fontWeight: 800, fontSize: 18, color: '#66ff99' }}>{plan?.amount?.toFixed(2)} USDT</span>
            </div>

            {insufficient && selectedBalance <= 0 && (
              <Alert variant="danger" style={{ background: 'rgba(200,50,50,0.15)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', fontSize: 13 }}>
                {selectedLabel} has <strong>0.00 USDT</strong>. {otherBalance >= plan?.amount
                  ? <>Select <strong>{otherWalletLabel}</strong> instead.</>
                  : <>Top it up before continuing.</>}
              </Alert>
            )}
            {insufficient && selectedBalance > 0 && (
              <Alert variant="danger" style={{ background: 'rgba(200,50,50,0.15)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', fontSize: 13 }}>
                Insufficient balance. Need <strong>{plan?.amount?.toFixed(2)} USDT</strong>, have <strong>{combinedBalance.toFixed(2)} USDT</strong> combined.
              </Alert>
            )}
            {error && (
              <Alert variant="danger" style={{ background: 'rgba(200,50,50,0.15)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', fontSize: 13 }}>
                {error}
              </Alert>
            )}

            <Button
              onClick={handleRenew}
              disabled={renewing || insufficient}
              style={{
                background: insufficient ? 'rgba(255,255,255,0.05)' : `linear-gradient(135deg, ${orange} 0%, #e08800 100%)`,
                border: 'none',
                color: insufficient ? '#555' : '#000',
                fontWeight: 700,
                fontSize: 15,
                padding: '12px',
                borderRadius: 8,
                marginTop: 8,
                width: '100%',
              }}
            >
              {renewing ? <Spinner animation="border" size="sm" /> : needsSplit ? `Renew R${plan?.nextRenewalCount} (Combined Payment)` : `Renew R${plan?.nextRenewalCount}`}
            </Button>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default RenewSlot;
